import { useState } from "react";
import { Card, FormStack, Input, Tabs, Text } from "@marekl/adminer/components";
import { CodeMirrorField } from "../../components/CodeMirrorField";
import { smsHint } from "../../lib/sms";
import type { EditorState } from "./editorState";
import styles from "./TemplateDetailPage.module.css";

type CodeTab = "meta" | "html" | "text";

interface CodeTemplateEditorProps {
  state: EditorState;
  onChange: (patch: Partial<EditorState>) => void;
  /** An SMS template is one body and nothing else. */
  isSMS?: boolean;
}

/**
 * Editor for `kind=code` templates.
 *
 * For e-mail that is subject and preheader, an HTML body and a plain-text
 * one, each in its own tab. An SMS has none of those things — no subject, no
 * preheader, no HTML — so it is a single body, and the tabs disappear rather
 * than standing there offering fields the renderer would refuse.
 */
export function CodeTemplateEditor({ state, onChange, isSMS = false }: CodeTemplateEditorProps) {
  const [tab, setTab] = useState<CodeTab>("html");

  if (isSMS) {
    return (
      <Card>
        <Card.Header title="Message text" />
        <Card.Content>
          <div className={styles.codeEditorStack}>
            <CodeMirrorField
              label="Text"
              language="none"
              value={state.textBody}
              onChange={(v) => onChange({ textBody: v })}
              height="320px"
              placeholder="Your car is on its way…"
            />
            {/* What it will cost, on the template rather than only at send
                time: a template that quietly runs to three segments is three
                times the bill on every message rendered from it. The count is
                of the template source, so a long {{.Data.name}} can still
                push a rendered message over — it is a guide, not a promise. */}
            <Text variant="muted" className={styles.editorNote}>
              {smsHint(state.textBody)} — before variables are filled in.
            </Text>
            <Text variant="muted" className={styles.editorNote}>
              Data is available as{" "}
              <Text as="span" variant="mono">
                {"{{.Data.field}}"}
              </Text>
              . Shared parts are included with{" "}
              <Text as="span" variant="mono">
                {'{{template "set/file.txt" .}}'}
              </Text>
              ; changing one only affects future sends.
            </Text>
          </div>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header title="Template content" />
      <Card.Content>
        <div className={styles.codeEditorStack}>
          <Tabs
            aria-label="Template parts"
            tabs={[
              { id: "meta", label: "Subject and preheader" },
              { id: "html", label: "HTML" },
              { id: "text", label: "Text" },
            ]}
            value={tab}
            onChange={(v) => setTab(v as CodeTab)}
          />

          {tab === "meta" && (
            <FormStack>
              <Input
                label="Subject"
                value={state.subject}
                onChange={(e) => onChange({ subject: e.target.value })}
                hint="Go template — data is available as {{.Data.field}}."
              />
              <Input
                label="Preheader"
                value={state.preheader}
                onChange={(e) => onChange({ preheader: e.target.value })}
                hint="Short text shown in inbox previews after the subject."
              />
            </FormStack>
          )}

          {tab === "html" && (
            <CodeMirrorField
              label="HTML body"
              value={state.htmlBody}
              onChange={(v) => onChange({ htmlBody: v })}
              height="480px"
              placeholder="<!doctype html>…"
            />
          )}

          {tab === "text" && (
            <CodeMirrorField
              label="Text body"
              language="none"
              value={state.textBody}
              onChange={(v) => onChange({ textBody: v })}
              height="480px"
              placeholder="Plain-text variant of the message…"
            />
          )}

          <Text variant="muted" className={styles.editorNote}>
            Shared parts (partials) are included by writing{" "}
            <Text as="span" variant="mono">
              {'{{template "set/file.html" .}}'}
            </Text>
            . Changing a partial only affects future sends.
          </Text>
        </div>
      </Card.Content>
    </Card>
  );
}
